import { Component } from 'react'
import { connect } from 'react-redux'
import { Modal } from 'reactstrap'
import PropTypes from 'prop-types'

import { SidebarPageWrapper } from '../../@paco/components'
import { LeaveOfAbsencesCalendarLegend, PacoLogsModal, Sidebar } from '../../@paco/compositions'
import { ConnectedHelpTooltip } from '../../@paco/connectors'
import checkPermission from '../../@paco/helpers/permissions/checkPermission'
import trans from '../../@paco/helpers/trans'
import { getEventLogsByEntityUuid } from '../../@paco/redux/eventLogs/eventLogsActions'
import {
    addLeaveOfAbsence, approveLeaveOfAbsence,
    declineLeaveOfAbsence,
    deleteLeaveOfAbsence,
    patchLeaveOfAbsence,
} from '../../@paco/redux/leaveOfAbsences/leaveOfAbsencesActions'
import { setError } from '../../@paco/redux/leaveOfAbsences/leaveOfAbsencesReducer'
import { setToast } from '../../@paco/redux/toasts/toastsReducer'
import { ToastType } from '../../@paco/types'

import ButtonAuthenticate from '../../components/ButtonAuthenticate/ButtonAuthenticate'
import NewFilters from '../../components/Filters/Filters'
import DeleteResourceForm from '../../components/forms/DeleteResourceForm/DeleteResourceForm'
import SearchBar from '../../components/SearchBar/SearchBar'
import {
    LEAVE_OF_ABSENCE_REQUESTS_CALENDAR,
    LEAVE_OF_ABSENCES_REQUEST_APPROVED,
    LEAVE_OF_ABSENCES_REQUEST_CLOSED,
    LEAVE_OF_ABSENCES_REQUEST_OPEN,
} from '../../constants'
import { removeLocationQueries, scrollToElementAndCenter } from '../../helpers'
import { translate } from '../../helpers/translations/translator'
import { LeaveOfAbsenceEditType } from '../../models'
import { getLeaveOfAbsenceRequests } from '../../redux/absences-ts/absencesActions'
import { setEmployeeSearch, setFilter } from '../../redux/filter/filterActions'
import { setStartAndEndDate } from '../../redux/weekNavigator/weekNavigatorActions'
import CalendarToolbar from '../Schedule/components/CalendarToolbar/CalendarToolbar'
import Days from './components/Days/Days'
import AddLeaveOfAbsenceForm from './forms/AddLeaveOfAbsenceForm'
import DeclineLeaveOfAbsenceForm from './forms/DeclineLeaveOfAbsenceForm'
import ResolveLeaveOfAbsenceForm from './forms/ResolveLeaveOfAbsenceForm/ResolveLeaveOfAbsenceForm'

import './LeaveOfAbsencesCalendar.scss'

const getFocusAbsence = () => window.location.search.split('?focusAbsence=')[1] || ''

class LeaveOfAbsenceCalendar extends Component {
    state = {
        activeAbsence: null,
        resolveAbsenceForm: false,
        declineForm: false,
        addLeaveOfAbsenceForm: false,
        focusAbsence: null,
        removeForm: false,
        logsModal: false,
    }

    componentDidMount() {
        this.getLeaveOfAbsences()
        const absenceId = getFocusAbsence()
        window.addEventListener('mousedown', this.onWindowClick)

        this.setState({
            focusAbsence: absenceId,
        })
    }

    componentDidUpdate(prevProps) {
        const { focusAbsence } = this.state
        const activeDepartmentOptions = this.props.departmentOptions.filter(option => option.isChecked)
        const prevActiveDepartmentOptions = prevProps.departmentOptions.filter(option => option.isChecked)

        if (focusAbsence) {
            const element = document.getElementById(`absence-${focusAbsence}`)

            if (element) scrollToElementAndCenter(element)
        }

        if (activeDepartmentOptions.length !== prevActiveDepartmentOptions.length) {
            this.getLeaveOfAbsences()
        }

        if (this.props.leaveOfAbsencesError && this.props.leaveOfAbsencesError !== prevProps.leaveOfAbsencesError) {
            this.props.dispatch(setToast({
                text: this.props.leaveOfAbsencesError,
                type: ToastType.fail,
                shouldFadeOutAutomatically: false,
            }))

            this.props.dispatch(setError(''))
        }
    }

    componentWillUnmount() {
        window.removeEventListener('mousedown', this.onWindowClick)
    }

    onWindowClick = () => {
        // Remove queries after first interaction from user, so we don't get any
        // repeating "focus animations".
        removeLocationQueries()
        this.setState({
            focusAbsence: '',
        })
        window.removeEventListener('mousedown', this.onWindowClick)
    }

    dispatchDeclineLeaveOfAbsence = async (declineComment) => {
        const { activeAbsence } = this.state

        this.setState({
            resolveAbsenceForm: false,
            declineForm: false,
            activeAbsence: null,
        })

        await this.props.dispatch(declineLeaveOfAbsence(
            activeAbsence,
            declineComment,
        ))

        this.getLeaveOfAbsences()
    }

    dispatchEditLeaveOfAbsence = async (
        data,
        periodsWithHours,
        weeksWithHours,
        leaveType,
    ) => {
        const { activeAbsence } = this.state

        this.setState({
            resolveAbsenceForm: false,
            activeAbsence: null,
        })

        await this.props.dispatch(patchLeaveOfAbsence(
            activeAbsence,
            data,
            activeAbsence.leaveOfAbsenceHours,
            weeksWithHours,
            leaveType,
        ))

        this.getLeaveOfAbsences()
    }

    dispatchApproveLeaveOfAbsence = async (
        weeksWithHours,
        leaveType,
        data,
    ) => {
        const { activeAbsence } = this.state

        this.setState({
            resolveAbsenceForm: false,
            activeAbsence: null,
        })

        await this.props.dispatch(approveLeaveOfAbsence(
            activeAbsence,
            data,
            activeAbsence.leaveOfAbsenceHours,
            weeksWithHours,
            leaveType,
        ))

        this.getLeaveOfAbsences()
    }

    dispatchAddLeaveOfAbsence = async (
        userId,
        data,
        weeksWithHours,
        comment,
        leaveType,
    ) => {
        this.setState({
            addLeaveOfAbsenceForm: false,
        })

        await this.props.dispatch(addLeaveOfAbsence(
            userId,
            data,
            weeksWithHours,
            leaveType,
            comment,
        ))

        this.getLeaveOfAbsences()
    }

    dispatchOpenLogsModal = (id) => {
        this.props.dispatch(getEventLogsByEntityUuid(id))
        this.setState({
            logsModal: true,
        })
    }

    dispatchCloseLogsModal = () => {
        this.setState({
            logsModal: false,
        })
    }

    dispatchSetFilters = (filter) => {
        this.props.dispatch(setFilter(filter, this.getLeaveOfAbsences))
    }

    getLeaveOfAbsences = () => {
        const status = this.getStatus()
        this.props.dispatch(getLeaveOfAbsenceRequests(LEAVE_OF_ABSENCE_REQUESTS_CALENDAR, status))
    }

    getStatus = () => {
        const { showDenied } = this.props.filter.absences
        const status = [LEAVE_OF_ABSENCES_REQUEST_OPEN, LEAVE_OF_ABSENCES_REQUEST_APPROVED]

        if (showDenied) status.push(LEAVE_OF_ABSENCES_REQUEST_CLOSED)

        return status
    }

    addResolveAbsenceForm = (absence) => {
        this.setState({
            resolveAbsenceForm: true,
            activeAbsence: absence,
        })
    }

    dispatchRemoveLeaveOfAbsence = async () => {
        this.setState({ removeForm: false })
        await this.props.dispatch(deleteLeaveOfAbsence(this.state.activeAbsence))
        this.getLeaveOfAbsences()
    }

    onSubmitLeaveOfAbsenceForm = (
        editType,
        data,
        payrollPeriodsWithHours,
        weeksWithHours,
        leaveType,
    ) => {
        switch (editType) {
            case LeaveOfAbsenceEditType.deny:
                this.addDeclineLeaveOfAbsenceForm()
                break
            case LeaveOfAbsenceEditType.delete:
                this.setState({
                    removeForm: true,
                    resolveAbsenceForm: false,
                })
                break
            case LeaveOfAbsenceEditType.accept:
                this.dispatchApproveLeaveOfAbsence(
                    weeksWithHours,
                    leaveType,
                    data,
                )
                break
            default:
                this.dispatchEditLeaveOfAbsence(
                    data,
                    payrollPeriodsWithHours,
                    weeksWithHours,
                    leaveType,
                )
                break
        }
    }

    addDeclineLeaveOfAbsenceForm = () => {
        this.setState({
            resolveAbsenceForm: false,
            declineForm: true,
        })
    }

    addLeaveOfAbsence = () => {
        this.setState({
            addLeaveOfAbsenceForm: true,
        })
    }

    setStartAndEndDate = (selectedDate, startDate, endDate) => {
        this.props.dispatch(setStartAndEndDate(selectedDate, startDate, endDate))
        this.getLeaveOfAbsences()
    }

    onSearchBarChange = (value) => {
        this.props.dispatch(setEmployeeSearch(value))
        this.getLeaveOfAbsences()
    }

    renderDays(selectedDays, mobile = false) {
        const { focusAbsence } = this.state
        const {
            loading,
            leaveOfAbsencesLoading,
            calendarLeaveOfAbsencesRequests,
            timeMode,
            filter,
            lastChangedLeaveOfAbsenceId,
            syncLoketRequest,
        } = this.props
        const { calendarViewProps } = filter.absences
        const focusId = focusAbsence || lastChangedLeaveOfAbsenceId

        return (
            <Days
                absences={calendarLeaveOfAbsencesRequests}
                focusAbsenceId={! loading && ! leaveOfAbsencesLoading ? focusId : ''}
                showHeader={! mobile}
                mobile={mobile}
                selectedDays={selectedDays}
                loading={loading || leaveOfAbsencesLoading || syncLoketRequest}
                onAbsenceClick={this.addResolveAbsenceForm}
                mode={timeMode}
                filter={calendarViewProps}
            />
        )
    }

    renderLeaveOfAbsences() {
        const {
            activeAbsence,
            resolveAbsenceForm,
            declineForm,
            addLeaveOfAbsenceForm,
            removeForm,
        } = this.state
        const {
            selectedDays,
            selectedDate,
            employees,
            permissions,
            pacoPayrollPeriods,
            payrollPeriods,
            currentUser,
            eventLogs,
            eventLogsLoading,
        } = this.props

        const existsInLoket = activeAbsence?.leaveOfAbsenceHours?.some((hours) => hours.loketIds?.length)

        return (
            <>
                <div className="calendar-days-desktop">
                    {this.renderDays(selectedDays)}
                </div>
                <div className="calendar-days-mobile leave-of-absences-calendar-days-mobile">
                    {this.renderDays([selectedDate], true)}
                </div>
                <div className="calendar-days-mobile" />
                <Modal size="lg" isOpen={resolveAbsenceForm} className="form-resolve-leave-of-absence">
                    <ResolveLeaveOfAbsenceForm
                        absence={activeAbsence}
                        currentUserRole={currentUser.role}
                        permissions={permissions}
                        pacoPayrollPeriods={pacoPayrollPeriods}
                        payrollPeriods={payrollPeriods}
                        onSubmit={this.onSubmitLeaveOfAbsenceForm}
                        onCancel={() => this.setState({ resolveAbsenceForm: false })}
                        onShowLogsClick={() => this.dispatchOpenLogsModal(activeAbsence.id)}
                    />
                </Modal>
                <Modal size="lg" isOpen={declineForm} className="form-decline-leave-of-absence">
                    <DeclineLeaveOfAbsenceForm
                        onSubmit={this.dispatchDeclineLeaveOfAbsence}
                        onCancel={() => this.setState({
                            declineForm: false,
                            activeAbsence: null,
                        })}
                    />
                </Modal>
                <Modal size="lg" isOpen={addLeaveOfAbsenceForm} className="form-add-leave-of-absence">
                    <AddLeaveOfAbsenceForm
                        employees={employees}
                        pacoPayrollPeriods={pacoPayrollPeriods}
                        payrollPeriods={payrollPeriods}
                        permissions={permissions}
                        onSubmit={this.dispatchAddLeaveOfAbsence}
                        onCancel={() => this.setState({ addLeaveOfAbsenceForm: false })}
                    />
                </Modal>
                <Modal size="lg" isOpen={removeForm} className="form-decline-leave-of-absence">
                    <DeleteResourceForm
                        title={translate('pages.absences.deleteLeaveOfAbsence')}
                        intro={
                            existsInLoket
                                ? translate('pages.absences.deleteLeaveOfAbsenceWarningWithLoketInfo')
                                : translate('pages.absences.deleteLeaveOfAbsenceWarning')
                        }
                        onSubmit={() => this.dispatchRemoveLeaveOfAbsence(this.state.absenceData)}
                        onCancel={() => this.setState({ removeForm: false, absenceData: null })}
                    />
                </Modal>
                <PacoLogsModal
                    isLoading={eventLogsLoading}
                    isOpen={this.state.logsModal && ! eventLogsLoading}
                    title={translate('logs.leaveOfAbsenceLog')}
                    emptyMessage={translate('logs.emptyLeaveOfAbsenceLog')}
                    logs={eventLogs}
                    onClose={this.dispatchCloseLogsModal}
                />
            </>
        )
    }

    render() {
        const {
            payrollPeriods,
            departments,
            filter,
            timeMode,
            permissions,
            roles,
            employmentTypes,
        } = this.props
        const { employeeSearch } = filter
        const canAddLeaveOfAbsence = checkPermission(permissions, 'add-new-leave-of-absences')
            || checkPermission(permissions, 'add-new-employee-leave-of-absences')
            || checkPermission(permissions, 'add-new-junior-manager-leave-of-absences')

        return (
            <>
                <SidebarPageWrapper>
                    <Sidebar>
                        <NewFilters
                            departments={departments}
                            filter={filter}
                            roles={roles}
                            employmentTypes={employmentTypes}
                            button={canAddLeaveOfAbsence ? (
                                <ButtonAuthenticate
                                    text={translate('pages.absences.addLeaveOfAbsence')}
                                    onClick={this.addLeaveOfAbsence}
                                />
                            ) : null}
                            filterBlocks={[
                                'show-denied-leave-of-absence-requests',
                                'calendar-view-props',
                                'user-types',
                                'contract-types',
                                'main-department',
                            ]}
                            onChange={this.dispatchSetFilters}
                        />

                        <LeaveOfAbsencesCalendarLegend className="leave-of-absences-calendar__legend" />
                    </Sidebar>
                    <main className="leave-of-absences-calendar page">
                        <div className="leave-of-absences-calendar__list-top">
                            <CalendarToolbar
                                payrollPeriods={payrollPeriods}
                                timeMode={timeMode}
                                setStartAndEndDate={this.setStartAndEndDate}
                                afterDateChange={this.getLeaveOfAbsences}
                            />
                            <div className="leave-of-absences-calendar__list-top-right">
                                <SearchBar placeholder={translate('common.searchEmployee')} startVal={employeeSearch} onChange={this.onSearchBarChange} />
                                <ConnectedHelpTooltip
                                    index={0}
                                    route="leave-of-absences-calendar"
                                    subTitle={trans('help.absences.viewOptions.title')}
                                    text={trans('help.absences.viewOptions.text')}
                                    title={trans('help.absences.title')}
                                    showMobileInfoWarning
                                    className="leave-of-absences-calendar__view-options-help-tooltip"
                                />
                            </div>
                        </div>
                        {this.renderLeaveOfAbsences()}
                        <ConnectedHelpTooltip
                            index={1}
                            route="leave-of-absences-calendar"
                            subTitle={trans('help.absences.addLeaveOfAbsence.title')}
                            text={trans('help.absences.addLeaveOfAbsence.text')}
                            title={trans('help.absences.title')}
                            className="leave-of-absences-calendar__add-leave-of-absence-help-tooltip"
                        />
                        <ConnectedHelpTooltip
                            index={2}
                            route="leave-of-absences-calendar"
                            subTitle={trans('help.absences.leaveOfAbsencesFilters.title')}
                            text={trans('help.absences.leaveOfAbsencesFilters.text')}
                            title={trans('help.absences.title')}
                            showMobileInfoWarning
                            className="leave-of-absences-calendar__filters-help-tooltip"
                        />
                        <ConnectedHelpTooltip
                            index={3}
                            route="leave-of-absences-calendar"
                            subTitle={trans('help.absences.leaveOfAbsenceItem.title')}
                            text={trans('help.absences.leaveOfAbsenceItem.text')}
                            title={trans('help.absences.title')}
                            className="leave-of-absences-calendar__leave-of-absence-item-help-tooltip"
                        />
                    </main>
                </SidebarPageWrapper>
                <ButtonAuthenticate
                    onClick={this.addLeaveOfAbsence}
                    mobile
                />
            </>
        )
    }
}

LeaveOfAbsenceCalendar.propTypes = {
    loading: PropTypes.bool.isRequired,
    leaveOfAbsencesError: PropTypes.string,
    leaveOfAbsencesLoading: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    departments: PropTypes.array.isRequired,
    departmentOptions: PropTypes.array.isRequired,
    employmentTypes: PropTypes.array.isRequired,
    pacoPayrollPeriods: PropTypes.array.isRequired,
    payrollPeriods: PropTypes.array.isRequired,
    roles: PropTypes.array.isRequired,
    permissions: PropTypes.array.isRequired,
    currentUser: PropTypes.object.isRequired,
    filter: PropTypes.object.isRequired,
    employees: PropTypes.array.isRequired,
    timeMode: PropTypes.string.isRequired,
    calendarLeaveOfAbsencesRequests: PropTypes.array.isRequired,
    selectedDays: PropTypes.array.isRequired,
    selectedDate: PropTypes.object.isRequired,
    lastChangedLeaveOfAbsenceId: PropTypes.string.isRequired,
    eventLogs: PropTypes.array,
    eventLogsLoading: PropTypes.bool.isRequired,
}

export default connect()(LeaveOfAbsenceCalendar)
